import React, {useCallback, useEffect} from "react";
import {connect, useSelector} from "react-redux";
import {RouteComponentProps, useLocation, withRouter} from "react-router";
import {css, Theme, useTheme} from "@emotion/react";
import {bindActionCreators, Dispatch} from "redux";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {Drawer} from "@pg-design/drawer";
import {backgroundColor, calculateRemSize, display, flex, flexDirection, h100, onDesktop, p, pt} from "@pg-design/helpers-css";
import {FiltersIcon} from "@pg-design/icons-module";
import {FormActions, IFormValuesAction} from "@pg-mono/form";
import {appendQueryString} from "@pg-mono/request";
import {Scenario} from "@pg-mono/rp-api-routes";

import {IRPStore} from "../../../app/rp_reducer";
import {MobileOptionsModal} from "../../../atoms/MobileOptionsModal";
import {useTransitioningHistoryPush} from "../../../hooks/use_transitioning_history_push";
import {resetPrivilegedOfferList} from "../../../offer/list/actions/fetch_privileged_offer_list";
import {resetSearchForm, searchFormActions} from "../../../search/actions/search_form_actions";
import {MobileSearchFilters} from "../../../search/components/MobileSearchFilters";
import {MobileTopSearchTriggers} from "../../../search/components/MobileTopSearchTriggers";
import {SearchFiltersWithAccordion} from "../../../search/components/SearchFiltersWithAccordion";
import {searchFormFields} from "../../../search/constants/search_form_constants";
import {useCountSearchFilters} from "../../../search/hooks/use_count_search_filters";
import useFilter from "../../../search/hooks/use_filter";
import useMobileFilters from "../../../search/hooks/use_mobile_filters";
import useSearchAndFilterUpdate from "../../../search/hooks/use_search_and_filter_update";
import {ISearchFiltersFormValues} from "../../../search/types/ISearchFiltersFormValues";
import {SearchFormValuesType} from "../../../search/types/SearchFormValuesType";
import {getSearchFiltersByOfferType} from "../../../search/utils/get_search_filters_by_offer_type";
import {getStringQueryValuesFromFormValues} from "../../../search/utils/get_string_values_from_form_values";
import {useUserPreferences} from "../../../user/hooks/use_user_preferences";
import {ILatestQuery} from "../../../utils/latest_query";
import {ViewType} from "../../../view_type/ViewType";
import {getInvestmentOfferListLink, getInvestmentOfferListMapLink} from "../../utils/get_abroad_offer_redirect_link";
import {getParsedInvestmentOfferListUrl} from "../../utils/get_parsed_investment_offer_list_url";

interface IStateProps {
    formValues: SearchFormValuesType;
}

interface IActionsProps {
    formActions: FormActions<Record<keyof typeof searchFormFields, unknown>>;
    resetPrivilegedOfferList: typeof resetPrivilegedOfferList;
    resetSearchForm: (query: Record<string, string>) => Promise<IFormValuesAction<Record<string, unknown>> | undefined>;
}
interface IOwnProps extends RouteComponentProps<{}> {
    onAfterSubmit?: () => void;
    latestQuery: ILatestQuery;
    isAbroad: boolean;
    mapUrl?: string;
    listUrl?: string;
    isMap?: boolean;
}

interface IProps extends IOwnProps, IStateProps, IActionsProps {}

const InvestmentOfferSearchDrawerC = (props: IProps) => {
    const theme = useTheme();
    const viewType = useSelector((state: IRPStore) => state.viewType);
    const scenario = viewType.current === ViewType.FOR_INVESTMENT_LIST ? Scenario.OFFER_LIST : Scenario.OFFER_LIST_MAP;
    const {transitionHistory} = useTransitioningHistoryPush();
    const location = useLocation();
    const {saveUserPreferences} = useUserPreferences();

    useEffect(() => {
        const query = props.latestQuery as Record<string, string>;
        (async () => {
            await props.resetSearchForm(query);
        })();
    }, []);

    const {isMobileFiltersActive, closeMobileFilterModal, toggleMobileFilterModal, prevFilterState} = useMobileFilters(props);

    const onSubmit = useCallback(
        async (formValues: SearchFormValuesType) => {
            const stringFormQueryValues = getStringQueryValuesFromFormValues(formValues);

            await saveUserPreferences({
                filters: {
                    ...stringFormQueryValues
                }
            });

            const params = getParsedInvestmentOfferListUrl(location.pathname);
            const url = props.isMap ? getInvestmentOfferListMapLink(params) : getInvestmentOfferListLink(params);

            if (url) {
                const investmentUrl = appendQueryString(url, stringFormQueryValues);

                transitionHistory(investmentUrl);
            }
        },
        [scenario, props.latestQuery, props.isAbroad]
    );

    const {onAfterChange} = useSearchAndFilterUpdate({
        resetPrivilegedOfferList: resetPrivilegedOfferList,
        formValues: props.formValues,
        latestQuery: props.latestQuery,
        onSubmit: (formValues: SearchFormValuesType) => onSubmit(formValues)
    });

    const {isDrawerOpen, setDrawerOpen, closeDrawer, resetFilters} = useFilter({
        formValues: props.formValues,
        closeMobileFilterModal: closeMobileFilterModal,
        onAfterChange: onAfterChange,
        onSubmit: onSubmit
    });

    const searchFiltersConfig = getSearchFiltersByOfferType(props.formValues.type?.[0]);
    const {totalCount: numberOfSearchFilters, countBySlots} = useCountSearchFilters<ISearchFiltersFormValues>(props.formValues, searchFiltersConfig, {
        afterMountOnly: true
    });

    // save form values on modal open to reset those values when form is discarded (Esc, Close)
    const discardFiltersModal = () => {
        closeMobileFilterModal();
        if (prevFilterState.current != null) {
            props.formActions.update(prevFilterState.current);
        }
    };

    const mobileFiltersProps = {
        selectedRegionsCount: props.formValues["search"].regions ? props.formValues["search"].regions.length : 0,
        selectedFiltersCount: numberOfSearchFilters,
        onToggleFiltersClick: toggleMobileFilterModal,
        onToggleSortClick: () => null,
        latestQuery: props.latestQuery,
        firstSelectedRegion: props.formValues.search?.regions?.[0]?.name,
        mapUrl: props.mapUrl,
        listUrl: props.listUrl
    };

    return (
        <>
            <div css={filtersButtonsStyle}>
                <Button
                    size="small"
                    variant="filled_primary"
                    iconLeft={FiltersIcon}
                    css={[actionButtonStyle, display("none"), onDesktop(display("inline-flex"))]}
                    onClick={() => setDrawerOpen(true)}
                >
                    FILTRY
                    {numberOfSearchFilters > 0 ? (
                        <Badge variant="label_danger" css={filtersCountBadge}>
                            {numberOfSearchFilters}
                        </Badge>
                    ) : null}
                </Button>

                <Drawer isOpen={isDrawerOpen} onDrawerClose={closeDrawer} contentStyle={backgroundColor(theme.colors.background[100])}>
                    <Drawer.Header>Filtry</Drawer.Header>
                    <Drawer.Content css={[drawerContentStyle, h100]}>
                        <SearchFiltersWithAccordion
                            searchFiltersConfig={searchFiltersConfig}
                            searchFiltersCountBySlots={countBySlots}
                            values={props.formValues}
                            onChange={props.formActions.update}
                            onAfterChange={onAfterChange}
                            onSubmit={() => onSubmit(props.formValues)}
                            onClose={closeDrawer}
                            onResetForm={resetFilters}
                            currency={props.isAbroad}
                        />
                    </Drawer.Content>
                </Drawer>
                <MobileOptionsModal
                    modalHeading="Filtry"
                    isOpen={isMobileFiltersActive}
                    onModalClose={discardFiltersModal}
                    css={[flex(), flexDirection("column")]}
                >
                    <MobileSearchFilters
                        searchFiltersConfig={searchFiltersConfig}
                        searchFiltersCountBySlots={countBySlots}
                        values={props.formValues}
                        onAfterChange={onAfterChange}
                        onChange={props.formActions.update}
                        onSubmit={() => onSubmit(props.formValues)}
                        onMobileCloseModal={closeMobileFilterModal}
                        onMobileResetForm={resetFilters}
                    />
                </MobileOptionsModal>
                <MobileTopSearchTriggers {...mobileFiltersProps} isMap={viewType.current === ViewType.FOR_INVESTMENT_LIST_MAP} isAbroad={props.isAbroad} />
            </div>
        </>
    );
};

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.search.formValues
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators({resetPrivilegedOfferList, resetSearchForm}, dispatch)
    };
}

export const InvestmentOfferSearchDrawer = withRouter(
    connect<IStateProps, IActionsProps, IOwnProps, IRPStore>(mapStateToProps, mapActionsToProps)(InvestmentOfferSearchDrawerC)
);

const filtersButtonsStyle = (theme: Theme) => css`
    ${flex()};
    justify-content: end;
    gap: ${calculateRemSize(2)};
    ${pt(0.5)};
    @media (min-width: ${theme.breakpoints.md}) {
        ${pt(0)}
    }
`;
const actionButtonStyle = css`
    width: 18rem;
    overflow: visible;
`;
const filtersCountBadge = css`
    position: absolute;
    right: -${calculateRemSize(0.5)};
    top: -${calculateRemSize(0.5)};
`;
const drawerContentStyle = css`
    ${flex()};
    ${flexDirection("column")};
    ${p(0)};
    overflow: hidden;
`;
import React, {useCallback, useEffect} from "react";
import {connect, useSelector} from "react-redux";
import {RouteComponentProps, useLocation, withRouter} from "react-router";
import {css, Theme, useTheme} from "@emotion/react";
import {bindActionCreators, Dispatch} from "redux";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {Drawer} from "@pg-design/drawer";
import {backgroundColor, calculateRemSize, display, flex, flexDirection, h100, onDesktop, p, pt} from "@pg-design/helpers-css";
import {FiltersIcon} from "@pg-design/icons-module";
import {FormActions, IFormValuesAction} from "@pg-mono/form";
import {appendQueryString} from "@pg-mono/request";
import {Scenario} from "@pg-mono/rp-api-routes";

import {IRPStore} from "../../../app/rp_reducer";
import {MobileOptionsModal} from "../../../atoms/MobileOptionsModal";
import {useTransitioningHistoryPush} from "../../../hooks/use_transitioning_history_push";
import {resetPrivilegedOfferList} from "../../../offer/list/actions/fetch_privileged_offer_list";
import {resetSearchForm, searchFormActions} from "../../../search/actions/search_form_actions";
import {MobileSearchFilters} from "../../../search/components/MobileSearchFilters";
import {MobileTopSearchTriggers} from "../../../search/components/MobileTopSearchTriggers";
import {SearchFiltersWithAccordion} from "../../../search/components/SearchFiltersWithAccordion";
import {searchFormFields} from "../../../search/constants/search_form_constants";
import {useCountSearchFilters} from "../../../search/hooks/use_count_search_filters";
import useFilter from "../../../search/hooks/use_filter";
import useMobileFilters from "../../../search/hooks/use_mobile_filters";
import useSearchAndFilterUpdate from "../../../search/hooks/use_search_and_filter_update";
import {ISearchFiltersFormValues} from "../../../search/types/ISearchFiltersFormValues";
import {SearchFormValuesType} from "../../../search/types/SearchFormValuesType";
import {getSearchFiltersByOfferType} from "../../../search/utils/get_search_filters_by_offer_type";
import {getStringQueryValuesFromFormValues} from "../../../search/utils/get_string_values_from_form_values";
import {useUserPreferences} from "../../../user/hooks/use_user_preferences";
import {ILatestQuery} from "../../../utils/latest_query";
import {ViewType} from "../../../view_type/ViewType";
import {getInvestmentOfferListLink, getInvestmentOfferListMapLink} from "../../utils/get_abroad_offer_redirect_link";
import {getParsedInvestmentOfferListUrl} from "../../utils/get_parsed_investment_offer_list_url";

interface IStateProps {
    formValues: SearchFormValuesType;
}

interface IActionsProps {
    formActions: FormActions<Record<keyof typeof searchFormFields, unknown>>;
    resetPrivilegedOfferList: typeof resetPrivilegedOfferList;
    resetSearchForm: (query: Record<string, string>) => Promise<IFormValuesAction<Record<string, unknown>> | undefined>;
}
interface IOwnProps extends RouteComponentProps<{}> {
    onAfterSubmit?: () => void;
    latestQuery: ILatestQuery;
    isAbroad: boolean;
    mapUrl?: string;
    listUrl?: string;
    isMap?: boolean;
}

interface IProps extends IOwnProps, IStateProps, IActionsProps {}

const InvestmentOfferSearchDrawerC = (props: IProps) => {
    const theme = useTheme();
    const viewType = useSelector((state: IRPStore) => state.viewType);
    const scenario = viewType.current === ViewType.FOR_INVESTMENT_LIST ? Scenario.OFFER_LIST : Scenario.OFFER_LIST_MAP;
    const {transitionHistory} = useTransitioningHistoryPush();
    const location = useLocation();
    const {saveUserPreferences} = useUserPreferences();

    useEffect(() => {
        const query = props.latestQuery as Record<string, string>;
        (async () => {
            await props.resetSearchForm(query);
        })();
    }, []);

    const {isMobileFiltersActive, closeMobileFilterModal, toggleMobileFilterModal, prevFilterState} = useMobileFilters(props);

    const onSubmit = useCallback(
        async (formValues: SearchFormValuesType) => {
            const stringFormQueryValues = getStringQueryValuesFromFormValues(formValues);

            await saveUserPreferences({
                filters: {
                    ...stringFormQueryValues
                }
            });

            const params = getParsedInvestmentOfferListUrl(location.pathname);
            const url = props.isMap ? getInvestmentOfferListMapLink(params) : getInvestmentOfferListLink(params);

            if (url) {
                const investmentUrl = appendQueryString(url, stringFormQueryValues);

                transitionHistory(investmentUrl);
            }
        },
        [scenario, props.latestQuery, props.isAbroad]
    );

    const {onAfterChange} = useSearchAndFilterUpdate({
        resetPrivilegedOfferList: resetPrivilegedOfferList,
        formValues: props.formValues,
        latestQuery: props.latestQuery,
        onSubmit: (formValues: SearchFormValuesType) => onSubmit(formValues)
    });

    const {isDrawerOpen, setDrawerOpen, closeDrawer, resetFilters} = useFilter({
        formValues: props.formValues,
        closeMobileFilterModal: closeMobileFilterModal,
        onAfterChange: onAfterChange,
        onSubmit: onSubmit
    });

    const searchFiltersConfig = getSearchFiltersByOfferType(props.formValues.type?.[0]);
    const {totalCount: numberOfSearchFilters, countBySlots} = useCountSearchFilters<ISearchFiltersFormValues>(props.formValues, searchFiltersConfig, {
        afterMountOnly: true
    });

    // save form values on modal open to reset those values when form is discarded (Esc, Close)
    const discardFiltersModal = () => {
        closeMobileFilterModal();
        if (prevFilterState.current != null) {
            props.formActions.update(prevFilterState.current);
        }
    };

    const mobileFiltersProps = {
        selectedRegionsCount: props.formValues["search"].regions ? props.formValues["search"].regions.length : 0,
        selectedFiltersCount: numberOfSearchFilters,
        onToggleFiltersClick: toggleMobileFilterModal,
        onToggleSortClick: () => null,
        latestQuery: props.latestQuery,
        firstSelectedRegion: props.formValues.search?.regions?.[0]?.name,
        mapUrl: props.mapUrl,
        listUrl: props.listUrl
    };

    return (
        <>
            <div css={filtersButtonsStyle}>
                <Button
                    size="small"
                    variant="filled_primary"
                    iconLeft={FiltersIcon}
                    css={[actionButtonStyle, display("none"), onDesktop(display("inline-flex"))]}
                    onClick={() => setDrawerOpen(true)}
                >
                    FILTRY
                    {numberOfSearchFilters > 0 ? (
                        <Badge variant="label_danger" css={filtersCountBadge}>
                            {numberOfSearchFilters}
                        </Badge>
                    ) : null}
                </Button>

                <Drawer isOpen={isDrawerOpen} onDrawerClose={closeDrawer} contentStyle={backgroundColor(theme.colors.background[100])}>
                    <Drawer.Header>Filtry</Drawer.Header>
                    <Drawer.Content css={[drawerContentStyle, h100]}>
                        <SearchFiltersWithAccordion
                            searchFiltersConfig={searchFiltersConfig}
                            searchFiltersCountBySlots={countBySlots}
                            values={props.formValues}
                            onChange={props.formActions.update}
                            onAfterChange={onAfterChange}
                            onSubmit={() => onSubmit(props.formValues)}
                            onClose={closeDrawer}
                            onResetForm={resetFilters}
                            currency={props.isAbroad}
                        />
                    </Drawer.Content>
                </Drawer>
                <MobileOptionsModal
                    modalHeading="Filtry"
                    isOpen={isMobileFiltersActive}
                    onModalClose={discardFiltersModal}
                    css={[flex(), flexDirection("column")]}
                >
                    <MobileSearchFilters
                        searchFiltersConfig={searchFiltersConfig}
                        searchFiltersCountBySlots={countBySlots}
                        values={props.formValues}
                        onAfterChange={onAfterChange}
                        onChange={props.formActions.update}
                        onSubmit={() => onSubmit(props.formValues)}
                        onMobileCloseModal={closeMobileFilterModal}
                        onMobileResetForm={resetFilters}
                    />
                </MobileOptionsModal>
                <MobileTopSearchTriggers {...mobileFiltersProps} isMap={viewType.current === ViewType.FOR_INVESTMENT_LIST_MAP} isAbroad={props.isAbroad} />
            </div>
        </>
    );
};

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.search.formValues
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators({resetPrivilegedOfferList, resetSearchForm}, dispatch)
    };
}

export const InvestmentOfferSearchDrawer = withRouter(
    connect<IStateProps, IActionsProps, IOwnProps, IRPStore>(mapStateToProps, mapActionsToProps)(InvestmentOfferSearchDrawerC)
);

const filtersButtonsStyle = (theme: Theme) => css`
    ${flex()};
    justify-content: end;
    gap: ${calculateRemSize(2)};
    ${pt(0.5)};
    @media (min-width: ${theme.breakpoints.md}) {
        ${pt(0)}
    }
`;
const actionButtonStyle = css`
    width: 18rem;
    overflow: visible;
`;
const filtersCountBadge = css`
    position: absolute;
    right: -${calculateRemSize(0.5)};
    top: -${calculateRemSize(0.5)};
`;
const drawerContentStyle = css`
    ${flex()};
    ${flexDirection("column")};
    ${p(0)};
    overflow: hidden;
`;
